import {
  type DerivedEventFilterLegacy,
  DerivedEventTypeLegacy,
  type FilterLegacy,
  FilterTypeLegacy,
  type ActiveCohortBuilderItemLegacy,
  type InputFieldConfig,
  type InputOptionContextDataLegacy,
} from '@verantos-dev/types-cohorts';
import {
  Derived_eventContext,
  Merged_eventContext,
  Merged_event_filter_criterionContext,
  Sequence_eventContext,
  Instance_eventContext,
} from '@verantos-dev/util-cohort-builder-typescript-runtime';

import { getNextEventFilters } from './getNextEventFilter';
import { getNextFilters } from './getNextFilters';
import { getNewEventsForType } from '../../utils/getNewEventsForType';

export const getActiveCohortBuilderItemStateForDerivedEvent = (
  derivedEventContext: Derived_eventContext,
  codeCompletionOptions: string[][],
  nextActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy,
  inputOptionContextData: InputOptionContextDataLegacy
): ActiveCohortBuilderItemLegacy => {
  // Map the subject variable context to the options
  // TODO: @chris-may: Add handling for merged events
  const mergedEventContext = derivedEventContext.merged_event();
  if (mergedEventContext) {
    return getNextActiveCohortBuilderItemForMergedEventContext(
      mergedEventContext,
      codeCompletionOptions,
      nextActiveCohortBuilderItem,
      inputOptionContextData
    );
  }

  // Obtain the sequence event context
  const sequenceEventContext = derivedEventContext.sequence_event();
  if (sequenceEventContext) {
    return getNextActiveCohortBuilderItemForSequenceEventContext(
      sequenceEventContext,
      codeCompletionOptions,
      nextActiveCohortBuilderItem,
      inputOptionContextData
    );
  }

  // TODO: @chris-may: Add handling for instance events
  const instanceEventContext = derivedEventContext.instance_event();
  if (instanceEventContext) {
    return getNextActiveCohortBuilderItemForInstanceEventContext(
      instanceEventContext,
      codeCompletionOptions,
      nextActiveCohortBuilderItem,
      inputOptionContextData
    );
  }

  return nextActiveCohortBuilderItem;
};

const getNextActiveCohortBuilderItemForMergedEventContext = (
  mergedEventContext: Merged_eventContext,
  codeCompletionOptions: string[][],
  nextActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy,
  inputOptionContextData: InputOptionContextDataLegacy
): ActiveCohortBuilderItemLegacy => {
  const mergedEventCriteriaContexts = mergedEventContext.merged_event_criteria();

  if (mergedEventCriteriaContexts) {
    let nextEventFilters: DerivedEventFilterLegacy[] = [];
    let nextEventFiltersInputFieldConfigMap: Record<string, InputFieldConfig> = {};

    const { eventFilters, uniquenessFilters } = nextActiveCohortBuilderItem;

    // map existing event filter input fields to the next input field config map
    if (eventFilters.length > 0) {
      const eventFilterContexts = eventFilters.map((_, index) => {
        const filter = mergedEventCriteriaContexts.merged_event_criterion(index);
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });

      const { nextDerivedEventFilters, nextDerivedEventFilterConfigs } = getNextEventFilters(
        eventFilters,
        eventFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextEventFilters = nextDerivedEventFilters;
      nextEventFiltersInputFieldConfigMap = nextDerivedEventFilterConfigs;
    }

    // First time a name and merged event type are selected, we need to generate two empty event filters
    // Note that in this pass we are just generating the new event filters, not the input field config map
    // Because the next active cohort builder item will have new event filters, we will recursively call this function again
    // In the next pass we will be able to generate the correct DSL query first to get the parse tree context we need
    // to generate values in the input field config map
    if (eventFilters.length === 0) {
      nextEventFilters = getNewEventsForType(DerivedEventTypeLegacy.MERGED);
    }

    // If uniqueness filters exist, update the simple event input fields
    let nextUniquenessFilters: FilterLegacy[] = [];
    let nextUniquenessInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (uniquenessFilters && uniquenessFilters.length > 0) {
      const uniquenessFilterContexts = uniquenessFilters.map((_, index) => {
        return mergedEventContext.merged_event_filter_criterion() as Merged_event_filter_criterionContext;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.UNIQUENESS,
        uniquenessFilters,
        uniquenessFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextUniquenessFilters = nextFilters;
      nextUniquenessInputFieldConfigMap = nextInputFieldConfigMap;
    }

    return {
      ...nextActiveCohortBuilderItem,
      eventFilters: nextEventFilters,
      uniquenessFilters: nextUniquenessFilters,
      inputFieldConfigMap: {
        ...nextEventFiltersInputFieldConfigMap,
        ...nextUniquenessInputFieldConfigMap,
      },
    };
  }

  return nextActiveCohortBuilderItem;
};

const getNextActiveCohortBuilderItemForSequenceEventContext = (
  sequenceEventContext: Sequence_eventContext,
  codeCompletionOptions: string[][],
  nextActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy,
  inputOptionContextData: InputOptionContextDataLegacy
): ActiveCohortBuilderItemLegacy => {
  // if no filters exists, return the subject variable as is
  const sequenceEventCriteriaContexts = sequenceEventContext.sequence_event_criteria();

  if (sequenceEventCriteriaContexts) {
    let nextEventFilters: DerivedEventFilterLegacy[] = [];
    let nextTemporalConstraints: FilterLegacy[] = [];
    let nextEventFiltersInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    let nextTemporalConstraintsInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    const { eventFilters, temporalConstraints } = nextActiveCohortBuilderItem;

    // map existing event filter input fields to the next input field config map
    if (eventFilters.length > 0) {
      const eventFilterContexts = eventFilters.map((_, index) => {
        const filter = sequenceEventCriteriaContexts.sequence_event_criterion(index);
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });

      const { nextDerivedEventFilters, nextDerivedEventFilterConfigs } = getNextEventFilters(
        eventFilters,
        eventFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );

      // check that next derived event filters have an event filter selected
      // If not, this would indicate that the selected event was deleted by the user
      // Make the last filter be selected in this case
      const hasEventFilterSelected = nextDerivedEventFilters.some(
        (filter) => filter.isSelected?.value === 'true'
      );
      nextEventFilters = !hasEventFilterSelected
        ? nextDerivedEventFilters.map((filter, i) => {
            if (i === nextDerivedEventFilters.length - 1) {
              return {
                ...filter,
                isSelected: {
                  id: filter.isSelected?.id ?? '',
                  value: 'true',
                },
              };
            }
            return filter;
          })
        : nextDerivedEventFilters;
      nextEventFiltersInputFieldConfigMap = nextDerivedEventFilterConfigs;
    }

    // First time a name and sequence event type are selected, we need to generate two empty event filters
    // Note that in this pass we are just generating the new event filters, not the input field config map
    // Because the next active cohort builder item will have new event filters, we will recursively call this function again
    // In the next pass we will be able to generate the correct DSL query first to get the parse tree context we need
    // to generate values in the input field config map
    if (eventFilters.length === 0) {
      nextEventFilters = getNewEventsForType(DerivedEventTypeLegacy.SEQUENCE);
    }

    // TODO: @chris-may: Add handling for temporal constraints
    if (temporalConstraints.length > 0) {
      const temporalConstraintContexts = temporalConstraints.map((_, index) => {
        const filter = sequenceEventCriteriaContexts.sequence_event_criterion(
          index + eventFilters.length
        );
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });

      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.TEMPORAL,
        temporalConstraints,
        temporalConstraintContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextTemporalConstraints = nextFilters;
      nextTemporalConstraintsInputFieldConfigMap = nextInputFieldConfigMap;
    }

    return {
      ...nextActiveCohortBuilderItem,
      temporalConstraints: nextTemporalConstraints,
      eventFilters: nextEventFilters,
      inputFieldConfigMap: {
        ...nextEventFiltersInputFieldConfigMap,
        ...nextTemporalConstraintsInputFieldConfigMap,
      },
    };
  }

  return nextActiveCohortBuilderItem;
};

const getNextActiveCohortBuilderItemForInstanceEventContext = (
  instanceEventContext: Instance_eventContext,
  codeCompletionOptions: string[][],
  nextActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy,
  inputOptionContextData: InputOptionContextDataLegacy
): ActiveCohortBuilderItemLegacy => {
  const instanceEventCriteriaContext = instanceEventContext.instance_event_criteria();

  if (instanceEventCriteriaContext) {
    let nextEventFilters: DerivedEventFilterLegacy[] = [];
    let nextEventFiltersInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    const { eventFilters } = nextActiveCohortBuilderItem;

    // map existing event filter input fields to the next input field config map
    if (eventFilters.length > 0) {
      const eventFilterContexts = eventFilters.map((_, index) => {
        const filter = instanceEventCriteriaContext.instance_event_criterion(index);
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });

      const { nextDerivedEventFilters, nextDerivedEventFilterConfigs } = getNextEventFilters(
        eventFilters,
        eventFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextEventFilters = nextDerivedEventFilters;
      nextEventFiltersInputFieldConfigMap = nextDerivedEventFilterConfigs;
    }

    // First time a name and instance event type are selected, we need to generate an empty event filter
    // Note that in this pass we are just generating the new event filter, not the input field config map
    // Because the next active cohort builder item will have a new event filter, we will recursively call this function again
    // In the next pass we will be able to generate the correct DSL query first to get the parse tree context we need
    // to generate values in the input field config map
    if (eventFilters.length === 0) {
      nextEventFilters = getNewEventsForType(DerivedEventTypeLegacy.INSTANCE);
    }

    return {
      ...nextActiveCohortBuilderItem,
      eventFilters: nextEventFilters,
      inputFieldConfigMap: {
        ...nextEventFiltersInputFieldConfigMap,
      },
    };
  }

  return nextActiveCohortBuilderItem;
};
